import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] py-12" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>

        <div className="glass-card p-8 sm:p-12">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Terms of Service
          </h1>
          <p className="text-[#A1A7B5] text-sm mb-8">Last Updated: April 15, 2026</p>

          <div className="space-y-8 text-[#C8CDD8] text-sm leading-relaxed">
            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Acceptance of Terms</h2>
              <p>By accessing or using this website ("Site") and purchasing products from Renew Labs ("Company," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use this Site.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Research Use Only</h2>
              <p className="mb-3">All products sold by Renew Labs are:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Intended strictly for laboratory research purposes only</li>
                <li>Not for human consumption</li>
                <li>Not for medical, diagnostic, or therapeutic use</li>
              </ul>
              <p className="mt-3">By purchasing from this Site, you represent and warrant that:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>You are a qualified individual or entity familiar with the safe handling of research materials</li>
                <li>You will use all products in compliance with applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. No Medical Advice</h2>
              <p className="mb-3">Content on this Site, including any AI-generated insights or biomarker analysis tools, is provided for educational and informational purposes only.</p>
              <p>We do not:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Provide medical advice</li>
                <li>Diagnose conditions</li>
                <li>Recommend treatments or dosages</li>
              </ul>
              <p className="mt-3">You should consult a licensed healthcare provider for any medical concerns.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Product Use and Liability</h2>
              <p className="mb-3">You agree that:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>You assume all responsibility for the proper use, storage, and handling of products</li>
                <li>You will not misuse products in any way inconsistent with their intended research purpose</li>
              </ul>
              <p className="mt-3">To the fullest extent permitted by law, Renew Labs shall not be liable for:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Any damages resulting from misuse of products</li>
                <li>Personal injury, illness, or adverse outcomes</li>
                <li>Indirect, incidental, or consequential damages</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Age Requirement</h2>
              <p>You must be at least 21 years old (or applicable legal age in your jurisdiction) to purchase from this Site.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Orders and Payments</h2>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>All orders are subject to acceptance and availability</li>
                <li>We reserve the right to refuse or cancel any order at our discretion</li>
                <li>Payments are processed securely through third-party providers (e.g., Stripe)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Refunds and Returns</h2>
              <p>Refunds and returns are governed by our Refund Policy, which is incorporated into these Terms.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Intellectual Property</h2>
              <p>All content on this Site — including logos, branding, text, and software — is the property of Renew Labs and may not be used without permission.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Limitation of Liability</h2>
              <p>To the maximum extent permitted by law: Renew Labs' total liability for any claim shall not exceed the amount paid for the product in question.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Indemnification</h2>
              <p className="mb-3">You agree to indemnify and hold harmless Renew Labs from any claims, damages, or expenses arising from:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Your misuse of products</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Modifications</h2>
              <p>We reserve the right to update these Terms at any time. Continued use of the Site constitutes acceptance of updated Terms.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>12. Governing Law</h2>
              <p>These Terms shall be governed by the laws of the State of Texas, without regard to conflict of law principles.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>13. Contact</h2>
              <p>For questions, contact:</p>
              <p className="mt-2 text-[#A1A7B5]">Email: <a href="mailto:support@getrenewlabs.com" className="text-[#00F0FF] hover:underline">support@getrenewlabs.com</a></p>
              <p className="text-[#A1A7B5]">Address: 8074 Precinct Ln Rd, Colleyville, TX 76034</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
